import { GridFrame } from 'Common/Components/GridFrame'
import {
  BaseSourceSiteWorkCentre,
  methodLabel,
  RUN_METHODS,
  staleTemplatesText,
  Status,
  STATUS_MAP,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import { Box, IconButton, Tooltip } from '@mui/material'
import { useMemo, useState } from 'react'
import { GridColDef } from '@mui/x-data-grid'
import {
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import PlayArrowOutlined from '@mui/icons-material/PlayArrowOutlined'
import { Button } from 'Common/Supplyvue-ui'
import { ActionItem } from 'Common/Components/MUITableAction'
import { Table } from 'Common/MUITableV2'
import { RunStatusChip } from 'Common/Components/RunStatusChip'
import { GridActionBar, JOINED_GRID_SX } from 'Common/Components/GridActionBar'
import { useNavigate } from 'react-router-dom'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined'

export const PlanTemplateSummaryTable = ({
  data,
  handleRunTemplates,
  isTemplateGenerating,
  handleRefresh,
  isRefetching,
}: {
  data: BaseSourceSiteWorkCentre[]
  handleRunTemplates: (selectedRows: GridRowId[]) => void
  isTemplateGenerating: boolean
  handleRefresh: () => void
  isRefetching: boolean
}) => {
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([])
  const navigate = useNavigate()
  const handleRowSelectionChange = (
    rowSelectionModel: GridRowSelectionModel
  ) => {
    setSelectedRows([...rowSelectionModel.ids])
  }

  const COLUMNS_WIDTH = 150

  // The results page compares the methods, so it opens once any has a
  // template to show.
  const isComplete = (
    params: GridRowParams<BaseSourceSiteWorkCentre>
  ): boolean =>
    RUN_METHODS.some((method) => params.row[STATUS_MAP[method]] === 'completed')

  const handleViewResults = (
    params: GridRowParams<BaseSourceSiteWorkCentre>
  ): void => {
    if (isComplete(params)) {
      navigate(`template-results/${params.row.workCentreKey}`)
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: COLUMNS_WIDTH,
        flex: 2,
      },
      {
        field: 'workcentreCode',
        headerName: 'Workcentre Code',
        width: COLUMNS_WIDTH,
        flex: 2,
      },
      {
        field: 'workcentreCapacity',
        headerName: 'Workcentre Capacity',
        width: COLUMNS_WIDTH,
        type: 'number',
        flex: 1,
      },
      {
        field: 'headSpaceBuffer',
        headerName: 'Head space',
        width: COLUMNS_WIDTH,
        type: 'number',
        flex: 1,
        // Stored as a fraction of capacity (0.1 = 10 %).
        valueFormatter: (value: number | null) =>
          value === null || value === undefined
            ? ''
            : `${(value * 100).toFixed(1)} %`,
      },
      // Read-only: a run generates every method, and the one to use is chosen
      // after comparing them, with "Use this method" on the results page.
      {
        field: 'preferredTemplate',
        headerName: 'Selected method',
        description:
          'The template replenishment uses. Change it on the results page after comparing the methods.',
        width: 140,
        valueGetter: (value?: string) => methodLabel(value ?? 'constrained'),
      },
      // One status per method: a run solves them all.
      ...RUN_METHODS.map(
        (method): GridColDef => ({
          field: STATUS_MAP[method],
          headerName: methodLabel(method),
          width: 120,
          headerAlign: 'center',
          align: 'center',
          // A completed template generated before its line's inputs changed
          // keeps its status, with a warning beside it.
          renderCell: (
            params: GridRenderCellParams<BaseSourceSiteWorkCentre>
          ) => {
            const stale = params.row.staleMethods?.includes(method)
            return (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <RunStatusChip status={params.value as Status} />
                {stale && (
                  <Tooltip title={staleTemplatesText(params.row, [method])}>
                    <WarningAmberOutlined
                      color="warning"
                      sx={{ fontSize: 16 }}
                      aria-label="Out of date"
                    />
                  </Tooltip>
                )}
              </Box>
            )
          },
        })
      ),
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        getActions: (params: GridRowParams<BaseSourceSiteWorkCentre>) => {
          return [
            <ActionItem
              label={'View Results'}
              clickHandler={() => {
                handleViewResults(params)
              }}
              showInMenu
              closeMenuOnClick={true}
              disabled={!isComplete(params)}
            />,
          ]
        },
      },
    ],
    [data]
  )

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
      }}
    >
      <GridActionBar
        hint={
          selectedRows.length
            ? `${selectedRows.length} work centre${
                selectedRows.length === 1 ? '' : 's'
              } selected`
            : `Select work centres to generate their templates (${RUN_METHODS.map(
                methodLabel
              )
                .join(', ')
                .toLowerCase()})`
        }
      >
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              aria-label="Refresh"
              onClick={handleRefresh}
              disabled={isRefetching}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          disabled={selectedRows.length === 0 || isTemplateGenerating}
          loading={isTemplateGenerating}
          loadingPosition="start"
          startIcon={<PlayArrowOutlined />}
          onClick={() => handleRunTemplates(selectedRows)}
        >
          Generate plan templates
        </Button>
      </GridActionBar>
      <GridFrame>
        <Table
          rows={data}
          columns={columns}
          columnGroupingModel={[
            {
              groupId: 'templateStatus',
              headerName: 'Template status',
              headerAlign: 'center',
              children: RUN_METHODS.map((method) => ({
                field: STATUS_MAP[method],
              })),
            },
          ]}
          checkboxSelection={true}
          onRowSelectionModelChange={handleRowSelectionChange}
          showCellVerticalBorder
          showColumnVerticalBorder
          pagination
          loading={isRefetching}
          onRowClick={handleViewResults}
          // Colours come from the theme; rows are clickable (they open results).
          sx={{
            ...JOINED_GRID_SX,
            height: '100%',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
          }}
          rowHeight={36}
          hideFooter={true}
        />
      </GridFrame>
    </Box>
  )
}
